import { inject, Injectable, NgZone, signal } from '@angular/core';
import { AuthService } from '@app/core/services';
import { AppConfigService } from '@app/core/config/app-config';
import { LiveChatConversationDto, LiveChatMessageDto } from '@app/data/dto';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';

export interface VisitorDraftUpdate {
  readonly conversationId: string;
  readonly text: string;
  readonly isTyping: boolean;
}

@Injectable({ providedIn: 'root' })
export class LiveChatRealtimeService {
  private readonly auth = inject(AuthService);
  private readonly config = inject(AppConfigService);
  private readonly zone = inject(NgZone);
  private connection?: signalR.HubConnection;
  private starting?: Promise<void>;

  readonly connected = signal(false);
  readonly available = signal(false);
  readonly conversationUpdated = new Subject<LiveChatConversationDto>();
  readonly messageCreated = new Subject<LiveChatMessageDto>();
  readonly visitorDraftUpdated = new Subject<VisitorDraftUpdate>();
  readonly dashboardUpdated = new Subject<void>();

  start(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) return Promise.resolve();
    if (this.starting !== undefined) return this.starting;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${this.config.config().apiUrl}/v1/realtime/livechat`, {
        accessTokenFactory: async () => {
          const current = this.auth.getAuthToken();
          if (current) return current;
          await this.auth.refreshSession();
          return this.auth.getAuthToken() ?? '';
        },
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect([0, 1500, 5000, 10000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    this.connection.on('ConversationUpdated', (conversation: LiveChatConversationDto) =>
      this.zone.run(() => this.conversationUpdated.next(conversation)),
    );
    this.connection.on('MessageCreated', (message: LiveChatMessageDto) =>
      this.zone.run(() => this.messageCreated.next(message)),
    );
    this.connection.on('VisitorDraftUpdated', (update: VisitorDraftUpdate) =>
      this.zone.run(() => this.visitorDraftUpdated.next(update)),
    );
    this.connection.on('DashboardUpdated', () =>
      this.zone.run(() => this.dashboardUpdated.next()),
    );
    this.connection.onreconnecting(() => this.zone.run(() => this.connected.set(false)));
    this.connection.onreconnected(() => this.zone.run(() => this.connected.set(true)));
    this.connection.onclose(() => this.zone.run(() => this.connected.set(false)));

    this.starting = this.connection
      .start()
      .then(() => this.zone.run(() => this.connected.set(true)))
      .finally(() => {
        this.starting = undefined;
      });
    return this.starting;
  }

  async setAvailability(available: boolean): Promise<void> {
    await this.start();
    await this.connection?.invoke('SetAvailability', available);
    this.available.set(available);
  }

  syncAvailability(available: boolean): void {
    this.available.set(available);
  }

  async setAgentTyping(conversationId: string, isTyping: boolean): Promise<void> {
    await this.start();
    await this.connection?.invoke('SetAgentTyping', conversationId, isTyping);
  }

  async stop(): Promise<void> {
    this.starting = undefined;
    await this.connection?.stop();
    this.connection = undefined;
    this.connected.set(false);
  }
}
